import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/charts/styles.css";
import "./global.css";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage, defaultStorage } from "aws-amplify/utils";

import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import React from "react";
import ReactDOM from "react-dom/client";

import { MantineProvider } from "@mantine/core";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router";

import * as SentryReact from "@sentry/react";

import App from "./App";
import IdleSessionHandler from "@/users/components/IdleSessionHandler";
import { TRPCProvider } from "@/common/utils/trpc";
import { theme } from "./theme";
import { queryClient } from "./queryClient";
import { PreferenceStorageProvider } from "./storage/PreferenceStorageContext";
import { CapacitorPreferenceStorage } from "./storage/CapacitorPreferenceStorage";
import { BrowserPreferenceStorage } from "./storage/BrowserPreferenceStorage";
import { EncryptedCacheStorageProvider } from "./storage/EncryptedCacheStorageProvider";

import { CapacitorCognitoTokenStorage } from "./storage/CapacitorCognitoTokenStorage";
import { AmplifyAuthListener } from "@/auth/components/AmplifyAuthListener";
import { LoggerProvider } from "@/logging/client/LoggerContext";
import { AmplifyAuthProvider } from "@/auth/components/AmplifyAuthContext";
import { ApiFetchProvider } from "./api/ApiFetchContext";
import { apiFetch } from "./api/apiFetch";
import { actionFetch } from "./api/actionFetch";
import { getCognitoUserPoolEndpoint } from "./cognitoEndpoint";
import { createUpdatePasskeyMFAPreference } from "@/auth/utils/updatePasskeyMFAPreference";
import { SearchStateProvider } from "@/common/hooks/useSearchState";
import {
	setUpTOTP,
	verifyTOTPSetup,
	updateMFAPreference,
	fetchMFAPreference,
	associateWebAuthnCredential,
	listWebAuthnCredentials,
	deleteWebAuthnCredential,
	fetchAuthSession,
	signOut,
} from "aws-amplify/auth";

const cognitoUserPoolEndpoint = getCognitoUserPoolEndpoint(
	import.meta.env.VITE_COGNITO_USER_POOL_ENDPOINT,
	window.location.origin,
);

// "call Amplify.configure as early as possible in your application’s life-cycle."
Amplify.configure({
	Auth: {
		Cognito: {
			userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
			userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
			// to support vite cognito proxy in dev container
			userPoolEndpoint: cognitoUserPoolEndpoint,
			identityPoolId: import.meta.env.PROD
				? import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID
				: "",
			allowGuestAccess: false,
			mfa: {
				smsEnabled: true,
				totpEnabled: true,
				status: "optional",
			},
			passwordless: {
				emailOtpEnabled: false,
				smsOtpEnabled: false,
				webAuthn: {
					userVerification: "required",
				},
			},
			passwordFormat: {
				minLength: 8,
				requireNumbers: true,
				requireLowercase: true,
				requireSpecialCharacters: true,
				requireUppercase: true,
			},
			loginWith: {
				username: true,
				email: true,
			},
			userAttributes: {
				email: {
					required: true,
				},
				phone_number: {
					required: false,
				},
			},
		},
	},
	Geo: {
		LocationService: {
			maps: {
				items: {
					"crm-dark": {
						// REQUIRED - Amazon Location Service Map resource name
						style: "VectorOpenDataStandardDark", // REQUIRED - String representing the style of map resource
					},
					"crm-light": {
						// REQUIRED - Amazon Location Service Map resource name
						style: "VectorOpenDataStandardLight", // REQUIRED - String representing the style of map resource
					},
					"crm-light-esri": {
						style: "VectorEsriNavigation",
					},
				},
				default: "crm-light", // REQUIRED - Amazon Location Service Map resource name to set as default
			},
			searchIndices: {
				items: ["crm-frontend"], // REQUIRED - Amazon Location Service Place Index name
				default: "crm-frontend", // REQUIRED - Amazon Location Service Place Index name to set as default
			},

			region: "us-east-1", // REQUIRED - Amazon Location Service Region
		},
	},
});

// BullBoard UI uses cookies. But it will be removed in the future - at that point we will be able to remove cookie storage.
const cookieStorage = new CookieStorage({
	domain: import.meta.env.PROD
		? "crm.villagecaregiving.com"
		: import.meta.env.VITE_CAPACITOR_DEV_SERVER_URL
			? new URL(import.meta.env.VITE_CAPACITOR_DEV_SERVER_URL).hostname
			: "localhost",
	sameSite: "lax",
	secure: import.meta.env.PROD,
	path: "/",
});

if (import.meta.env.VITE_CAPACITOR_BUILD === "true") {
	cognitoUserPoolsTokenProvider.setKeyValueStorage(
		new CapacitorCognitoTokenStorage(
			"vcg-cognito-",
			new CapacitorPreferenceStorage(),
			{
				error: (err) => {
					SentryReact.logger.error(err);
				},
			},
		),
	);
} else {
	cognitoUserPoolsTokenProvider.setKeyValueStorage({
		async setItem(key: string, value: string): Promise<void> {
			await Promise.all([
				defaultStorage.setItem(key, value),
				cookieStorage.setItem(key, value),
			]);
		},
		async getItem(key: string): Promise<string | null> {
			const defaultValue = await defaultStorage.getItem(key);
			if (defaultValue !== null) {
				return defaultValue;
			}
			return cookieStorage.getItem(key);
		},
		async removeItem(key: string): Promise<void> {
			await Promise.all([
				defaultStorage.removeItem(key),
				cookieStorage.removeItem(key),
			]);
		},
		async clear(): Promise<void> {
			await Promise.all([defaultStorage.clear(), cookieStorage.clear()]);
		},
	});
}

if (import.meta.env.VITE_SENTRY_DSN) {
	if (import.meta.env.VITE_CAPACITOR_BUILD === "true") {
		import("@sentry/capacitor").then((SentryCapacitor) => {
			SentryCapacitor.init(
				{
					dsn: import.meta.env.VITE_SENTRY_DSN,
					release: import.meta.env.VITE_APP_VERSION || "unknown",
					environment: import.meta.env.PROD ? "production" : "development",
					// Tracing & Session Replay Disabled on mobile to avoid
					// crashes, out of memory issues, and hangs
					tracesSampleRate: 0,
					enableLogs: true,
					integrations: [SentryReact.featureFlagsIntegration()],
				},
				SentryReact.init,
			);
		});
	} else {
		SentryReact.init({
			dsn: import.meta.env.VITE_SENTRY_DSN,
			release: import.meta.env.VITE_APP_VERSION || "unknown",
			environment: import.meta.env.PROD ? "production" : "development",
			integrations: [
				SentryReact.browserTracingIntegration(),
				SentryReact.replayIntegration(),
				SentryReact.featureFlagsIntegration(),
			],
			// Tracing
			tracesSampleRate: 0.2,
			tracePropagationTargets: [import.meta.env.VITE_API_URL],
			// Session Replay
			replaysSessionSampleRate: 0.1,
			replaysOnErrorSampleRate: 1.0,
			enableLogs: true,
		});
	}
}

window.addEventListener("vite:preloadError", () => {
	window.location.reload();
});

const preferenceStorage =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? new CapacitorPreferenceStorage()
		: new BrowserPreferenceStorage();

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<React.StrictMode>
			<NuqsAdapter>
				<MantineProvider theme={theme}>
					<ModalsProvider>
						<Notifications position="bottom-center" />
						<PreferenceStorageProvider storage={preferenceStorage}>
							<QueryClientProvider client={queryClient}>
								<ApiFetchProvider value={{ apiFetch, actionFetch }}>
									<TRPCProvider>
										<LoggerProvider>
											<AmplifyAuthProvider
												value={{
													fetchAuthSession,
													signOut,
													setUpTOTP,
													verifyTOTPSetup,
													updateMFAPreference,
													fetchMFAPreference,
													associateWebAuthnCredential,
													listWebAuthnCredentials,
													deleteWebAuthnCredential,
													updatePasskeyMFAPreference:
														createUpdatePasskeyMFAPreference({
															endpoint: cognitoUserPoolEndpoint,
															fetchAuthSession,
															fetchMFAPreference,
															fetchFn: fetch,
														}),
												}}
											>
												<EncryptedCacheStorageProvider>
													{/* <ReactQueryDevtools initialIsOpen={false} /> */}
													<SearchStateProvider>
														<BrowserRouter>
															<AmplifyAuthListener />
															<App />
														</BrowserRouter>
													</SearchStateProvider>

													<IdleSessionHandler />
												</EncryptedCacheStorageProvider>
											</AmplifyAuthProvider>
										</LoggerProvider>
									</TRPCProvider>
								</ApiFetchProvider>
							</QueryClientProvider>
						</PreferenceStorageProvider>
					</ModalsProvider>
				</MantineProvider>
			</NuqsAdapter>
		</React.StrictMode>,
	);
}
